import logger from "../utils/logger";
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as inspectionsService from '../services/supabase/inspections';

/**
 * Custom hook for managing inspections with Supabase
 * @param {string} propertyId - Optional property ID to filter inspections (null fetches all)
 */
export function useInspections(propertyId = null) {
  const { user } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  // Fetch inspections on mount and when user ID/propertyId changes
  // Wait for profile to be loaded before fetching (prevents race condition on page refresh)
  const fetchInspections = useCallback(async () => {
    if (!user) {
      setInspections([]);
      setLoading(false);
      return;
    }

    // Profile not required - user is enough for RLS
    // Removed profile check to prevent infinite waiting

    logger.log('[useInspections] Starting fetch...', { propertyId });
    const fetchStartTime = Date.now();
    let timeoutId;

    try {
      setLoading(true);
      setError(null);

      // Timeout to prevent infinite hanging
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Query timeout - sesja może być nieprawidłowa'));
        }, 10000);
      });

      let dataPromise;
      if (propertyId) {
        dataPromise = inspectionsService.getInspections(propertyId);
      } else {
        dataPromise = inspectionsService.getAllInspections();
      }

      const data = await Promise.race([dataPromise, timeoutPromise]);

      if (timeoutId) clearTimeout(timeoutId);

      if (!isMountedRef.current) {
        return;
      }

      setInspections(data);
      logger.log(`[useInspections] Fetch completed in ${Date.now() - fetchStartTime}ms`);
    } catch (err) {
      if (timeoutId) clearTimeout(timeoutId);

      if (isMountedRef.current) {
        console.error('[useInspections] Error fetching inspections:', err);
        setError(err.message);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [user?.id, propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    isMountedRef.current = true;
    fetchInspections();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchInspections]);

  const createInspection = async (inspectionData) => {
    try {
      const newInspection = await inspectionsService.createInspection(inspectionData);
      setInspections(prev => [newInspection, ...prev]);
      return { success: true, data: newInspection };
    } catch (err) {
      console.error('Error creating inspection:', err);
      return { success: false, error: err.message };
    }
  };

  const updateInspection = async (id, updates) => {
    try {
      const updatedInspection = await inspectionsService.updateInspection(id, updates);
      setInspections(prev =>
        prev.map(i => (i.id === id ? { ...i, ...updatedInspection } : i))
      );
      return { success: true, data: updatedInspection };
    } catch (err) {
      console.error('Error updating inspection:', err);
      return { success: false, error: err.message };
    }
  };

  const updateInspectionItem = async (itemId, updates) => {
    try {
      await inspectionsService.updateInspectionItem(itemId, updates);
      // Refresh to get updated data with relations
      await fetchInspections();
      return { success: true };
    } catch (err) {
      console.error('Error updating inspection item:', err);
      return { success: false, error: err.message };
    }
  };

  const deleteInspection = async (id) => {
    try {
      await inspectionsService.deleteInspection(id);
      setInspections(prev => prev.filter(i => i.id !== id));
      return { success: true };
    } catch (err) {
      console.error('Error deleting inspection:', err);
      return { success: false, error: err.message };
    }
  };

  const refresh = () => {
    fetchInspections();
  };

  return {
    inspections,
    loading,
    error,
    createInspection,
    updateInspection,
    updateInspectionItem,
    deleteInspection,
    refresh,
  };
}
